<template>
    <v-container fill-height fluid>
        <v-row>
            <v-col class="pa-0" xs="12" md="12">
                <v-card class="mx-auto px-2" outlined>
                <v-row>
                    <v-col xs="12" md="6">
                    <div class="px-2 primary--text font-weight-bold">
                        {{ $vuetify.lang.t("$vuetify.maira_questionnaire.questionnaire") }}
                    </div>
                    </v-col>
                    <v-col xs="12" md="6">
                    <v-btn
                        class="float-right"
                        color="primary"
                        @click="openCreateDialog"
                    >
                        {{ $vuetify.lang.t("$vuetify.maira_questionnaire.create_questionnaire") }}
                        <v-icon right class="ml-2">mdi-plus-circle-outline</v-icon>
                    </v-btn>
                    </v-col>
                </v-row>

                <v-divider></v-divider>

                <v-row>
                    <v-col cols="12" xs="12" md="4">
                    <v-text-field
                        v-model="search"
                        append-icon="mdi-magnify"
                        :label="$vuetify.lang.t('$vuetify.common.search_btn_txt')"
                        outlined
                        dense
                        single-line
                        hide-details
                    ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                        <v-select
                            v-model="activeFilter"
                            :items="[
                            { text: 'Active', value: true },
                            { text: 'Inactive', value: false }
                            ]"
                            :label="$vuetify.lang.t('$vuetify.common.status')"
                            item-text="text"
                            item-value="value"
                            outlined
                            dense
                        ></v-select>
                    </v-col>
                </v-row>

                <v-divider></v-divider>

                <v-data-table
                    :headers="headers"
                    :items="questionnaires"
                    :search="search"
                    :page.sync="page"
                    :items-per-page="itemsPerPage"
                    :loading="loading"
                    hide-default-footer
                >
                    <template v-slot:[`item.created_at`]="{ item }">
                    {{ formatDateTime(item.created_at) }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                    <ButtonGroup
                        :disabledEditButton="!$hasProjectAccessFor([$pRole.ADMIN])"
                        :disabledDeleteButton="!$hasProjectAccessFor([$pRole.ADMIN])"
                        :groupType="ACTION_BUTTON_GROUP"
                        :addButton="false"
                        :stopButton="false"
                        :startButton="false"
                        @onDeleteClick="deleteItem(item)"
                        @onEditClick="editItem(item)"
                        @onDetailsClick="showDetails(item)"
                    />
                    </template>
                </v-data-table>

                <DialogModal
                    v-model="deleteDialog"
                    :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
                    :message="$vuetify.lang.t('$vuetify.common.confirm_alert_msg_txt_11')"
                    :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
                    :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
                    @onConfirmation="deleteItemConfirm"
                    @onCancel="deleteDialog = false"
                />

                <v-row>
                    <v-col class="py-0" cols="12" md="12">
                    <v-divider></v-divider>
                    </v-col>
                    <v-col cols="12" xs="12" sm="3" md="3"></v-col>
                    <v-col cols="12" xs="12" sm="6" md="6">
                    <v-pagination
                        class="text-center"
                        v-model="page"
                        :length="pageCount"
                    ></v-pagination>
                    </v-col>
                    <v-col cols="12" offset-sm="1" offset-md="1" xs="3" sm="2" md="2">
                    <v-select
                        v-model="itemsPerPage"
                        :items="rows"
                        hide-details="auto"
                        outlined
                        dense
                    ></v-select>
                    </v-col>
                </v-row>
                </v-card>
            </v-col>
        </v-row>

        <!-- Details Modal -->
        <v-dialog v-model="detailsDialog" max-width="800px">
            <v-card class="elevation-0" v-if="selectedMairaQuestionnaire">
                <v-card-title class="font-weight-bold px-6 pt-5 pb-3">
                    {{ $vuetify.lang.t("$vuetify.account.basic_info_header_txt") }}
                </v-card-title>
                <v-divider></v-divider>

                <v-container class="py-2 px-6">
                    <v-row class="my-2">
                        <v-col cols="4" class="py-1 font-weight-bold">
                            {{ `${$vuetify.lang.t("$vuetify.maira_questionnaire.questionnaire_name")}` }}
                        </v-col>
                        <v-col cols="8" class="py-1 text-right font-weight-medium">
                            {{ selectedMairaQuestionnaire.name }}
                        </v-col>
                    </v-row>

                    <v-row class="my-2">
                        <v-col cols="4" class="py-1 font-weight-bold">
                            {{ `${$vuetify.lang.t("$vuetify.questionnaire.created_at")}` }}
                        </v-col>
                        <v-col cols="8" class="py-1 text-right font-weight-medium">
                            {{ formatDateTime(selectedMairaQuestionnaire.created_at) }}
                        </v-col>
                    </v-row>

                    <v-row class="my-2" v-if="selectedMairaQuestionnaire.updated_at">
                        <v-col cols="4" class="py-1 font-weight-bold">
                            {{ `${$vuetify.lang.t("$vuetify.table_header.updated_at")}` }}
                        </v-col>
                        <v-col cols="8" class="py-1 text-right font-weight-medium">
                            {{ formatDateTime(selectedMairaQuestionnaire.updated_at) }}
                        </v-col>
                    </v-row>

                    <v-row class="my-2">
                        <v-col cols="4" class="py-1 font-weight-bold">
                            {{ `${$vuetify.lang.t("$vuetify.common.status")}` }}
                        </v-col>
                        <v-col cols="8" class="py-1 text-right font-weight-medium">
                            <v-chip :color="selectedMairaQuestionnaire.active ? 'green' : 'red'" dark small>
                                {{ selectedMairaQuestionnaire.active ? 'Active' : 'Deactive' }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-text class="pt-0 px-6 pb-2">
                    <v-expansion-panels>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                {{ `${$vuetify.lang.t("$vuetify.maira_questionnaire.view_questionnaire_body")}` }}
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <div class="overflow-x-auto">
                                    <v-card outlined class="pa-3 grey lighten-3">
                                        <code class="d-block" style="white-space: pre-wrap;">{{
                                            JSON.stringify(selectedMairaQuestionnaire, null, 2) }}</code>
                                    </v-card>
                                </div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card-text>

                <v-card-actions class="px-6 pb-4">
                    <v-spacer></v-spacer>
                    <v-btn color="error" outlined @click="closeDetails">
                        {{ $vuetify.lang.t("$vuetify.close") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
            <v-card v-else class="elevation-0">
                <v-card-text>Loading questionnaire details...</v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogVisible" max-width="900px" persistent>
            <v-card>
                <v-card-title class="font-weight-bold">
                    {{
                        dialogMode === 'create'
                            ? $vuetify.lang.t("$vuetify.maira_questionnaire.create_questionnaire")
                            : $vuetify.lang.t("$vuetify.maira_questionnaire.update_questionnaire")
                    }}
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text>
                    <CreateMairaQuestionnaire
                        :key="dialogMode + (dialogVisible ? '-open' : '-closed')"
                        :mode="dialogMode"
                        :is-questionnaire-dialog-visible="dialogVisible" 
                        :questionnaire-data="dialogMode === 'edit' ? selectedMairaQuestionnaire : undefined"
                        @close="handleDialogClose"
                        @questionnaire-created="handleQuestionnaireCreated"
                        @questionnaire-updated="handleQuestionnaireUpdated"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import {
    MAIRA_QUESTIONNAIRE,
    GET_MAIRA_QUESTIONNAIRE,
    DELETE_MAIRA_QUESTIONNAIRE,
    UPDATE_MAIRA_QUESTIONNAIRE,
    ADD_ALERT
} from "@/store/_actiontypes";

import {
    CLEAR_SELECTED_QUESTIONNAIRE,
} from "@/store/_mutationtypes";
import { ACTION_BUTTON_GROUP } from "@/components/common/buttongroup/_buttongrouptypes";
import Api from "@/services/api";
import ButtonGroup from "../components/common/buttongroup/ButtonGroup.vue";
import DialogModal from "@/components/common/DialogModal";
import {formatDateTime} from "@/helpers/formatter";
import CreateMairaQuestionnaire from "./CreateMairaQuestionnaire.vue";

export default {
    components: { ButtonGroup, DialogModal, CreateMairaQuestionnaire },
    data() {
        return {
            dialogVisible: false,
            dialogMode: 'create',
            deleteDialog: false,
            headers: [
                {
                    text: this.$vuetify.lang.t("$vuetify.questionnaire.questionnaire_id"),
                    align: "start",
                    sortable: true,
                    value: "id",
                },
                {
                    text: this.$vuetify.lang.t("$vuetify.maira_questionnaire.questionnaire_name"),
                    align: "start",
                    sortable: true,
                    value: "name",
                },
                {
                    text: this.$vuetify.lang.t("$vuetify.table_header.created_at"),
                    align: "center",
                    sortable: true,
                    value: "created_at",
                },
                {
                    text: this.$vuetify.lang.t("$vuetify.common.actions"),
                    value: "actions",
                    sortable: false,
                },
            ],
            questionnaires: [],
            ACTION_BUTTON_GROUP: ACTION_BUTTON_GROUP,
            formatDateTime,
            rows: [5, 10, 20],
            search: "",
            page: 1,
            itemsPerPage: 10,
            loading: false,
            error: null,
            dialog: false,
            editedIndex: -1,
            editedItem: {
                id: 0,
                name: "",
            },
            selectedItem: null,
            selectedItemQuestionnaire: null,
            detailsDialog: false,
            deleteConfirmed: false,
            totalItems: 0,
            activeFilter: true,
        };
    },
    created() {},
    computed: {
        ...mapState({
            user: (state) => state.account.user,
            selectedProject: (state) => state.project.selectedProject,
            selectedMairaQuestionnaire: (state) => state.project.selectedMairaQuestionnaire,
        }),
        pageCount() {
            if (this.totalItems) {
                return Math.ceil(this.totalItems / this.itemsPerPage);
            }
            return 1;
        },
    },
    mounted() {
        this.GetQuestionnaireData(this.page, this.itemsPerPage);
    },
    methods: {
        ...mapActions("project", [
            MAIRA_QUESTIONNAIRE,
            GET_MAIRA_QUESTIONNAIRE,
            DELETE_MAIRA_QUESTIONNAIRE,
            UPDATE_MAIRA_QUESTIONNAIRE,
        ]),
        ...mapMutations("project", [
            CLEAR_SELECTED_QUESTIONNAIRE,
        ]),
        async GetQuestionnaireData(page = this.page, size = this.itemsPerPage) {
            try {
                this.loading = true;
                const project_id = this.selectedProject.id;
                const start = (page - 1) * size;

                const params = {
                    project_id,
                    start_date: null,
                    end_date: null,
                    active: this.activeFilter,
                    start,
                    size,
                };

                const response = await this.MAIRA_QUESTIONNAIRE(params);

                const resData = response.data.detail.response;
                this.questionnaires = resData.questionnaires || [];
                this.totalItems = resData.total_hits || 0;

            } catch (error) {
                console.error("Get error: ", error);
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },

        async editItem(item) {
            try {
                await this.GET_MAIRA_QUESTIONNAIRE({
                project_id: this.selectedProject.id,
                questionnaire_id: item.id
                });
                this.dialogMode = 'edit';
                this.dialogVisible = true;
            } catch (error) {
                console.error("Error fetching questionnaire details:", error);
            }
        },
        deleteItem(item) {
            this.selectedItemQuestionnaire = item;
            this.deleteDialog = true;
        },
        handleDialogClose() {
            this.dialogVisible = false;
            if (this.dialogMode === 'edit') {
                this.CLEAR_SELECTED_QUESTIONNAIRE();
            }
            this.dialogMode = 'create';
        },
        async deleteItemConfirm() {
            try {
                const project_id = this.selectedProject.id;
                const questionnaire_id = this.selectedItemQuestionnaire.id;

                await this.DELETE_MAIRA_QUESTIONNAIRE({ project_id, questionnaire_id });
                this.$store.dispatch(
                    `alert/${ADD_ALERT}`,
                    {
                        message: this.$vuetify.lang.t("$vuetify.maira_questionnaire.delete_success"),
                        color: "success",
                    },
                    { root: true }
                );
                this.deleteDialog = false;
                setTimeout(() => {
                this.GetQuestionnaireData(this.page, this.itemsPerPage);
                }, 1000);
            } catch (error) {
                console.error("Error deleting questionnaire:", error);
                this.error = error.message;
                this.deleteDialog = false;
            }
        },
        closeDelete() {
            this.deleteDialog = false;
            this.$nextTick(() => {
                this.editedItem = { id: 0, name: "" };
                this.editedIndex = -1;
            });
        },
        showDetails(item) {
            this.selectedItem = item;
            this.detailsDialog = true;
            this.fetchQuestionnaireDetails(item.id);
        },
        closeDetails() {
            this.detailsDialog = false;
            this.$store.commit("project/CLEAR_SELECTED_QUESTIONNAIRE");
        },
        async fetchQuestionnaireDetails(questionnairesId) {
            try {
                await this.GET_MAIRA_QUESTIONNAIRE({
                project_id: this.selectedProject.id,
                questionnaire_id: questionnairesId
                });
            } catch (error) {
                console.error("Error fetching questionnaire details:", error);
            }
        },
        handleCloseCreate() {
            this.$refs.createMairaQuestionnaire?.cancelQuestionnaire();
        },

        async handleQuestionnaireUpdated(response) {
            this.dialogVisible = false;
            this.dialogMode = 'create';
            setTimeout(() => {
                this.GetQuestionnaireData(this.page, this.itemsPerPage);
            }, 1000);
        },
        async handleQuestionnaireCreated(response) {
            this.dialogVisible = false;
            this.dialogMode = 'create';
            setTimeout(() => {
                this.GetQuestionnaireData(this.page, this.itemsPerPage);
            }, 1000);
        },
        openCreateDialog() {
            this.dialogMode = 'create';
            this.dialogVisible = true;
            this.CLEAR_SELECTED_QUESTIONNAIRE();
        },
    },
    watch: {
        page: {
            handler(newVal) {
                this.GetQuestionnaireData(this.page, this.itemsPerPage);
            },
            immediate: false,
        },
        itemsPerPage: {
            handler(newVal) {
                this.page = 1;
                this.GetQuestionnaireData(1, newVal);
            },
            immediate: false,
        },
        activeFilter: {
            handler() {
                this.page = 1;
                this.GetQuestionnaireData(1, this.itemsPerPage);
            },
            immediate: false,
        },
        dialog(val) {
            val || this.close();
        },
        deleteDialog(val) {
            val || this.closeDelete();
        },
    },
};
</script>